import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ChevronRight, FileUp, Gauge } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { API, PORTAL_URL, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";

export default function MakeTuning() {
  const { make } = useParams();
  const [data, setData] = useState(null);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    setData(null);
    setMiss(false);
    axios.get(`${API}/tuning-database/makes/${make}`).then((r) => setData(r.data)).catch(() => setMiss(true));
  }, [make]);

  // ---- SEO head: manufacturer-specific title, meta, canonical, structured data ----
  useEffect(() => {
    if (!data) return;
    document.title = `${data.make} Tuning & ECU Remapping — ${data.vehicle_count} Vehicles | Regal Tuning`;
    const desc = `${data.make} tuning database: verified Stage 1, Stage 2, Stage 3 and E85 ECU remap gains for ${data.vehicle_count} ${data.make} vehicles across ${data.model_count} models. Compare factory BHP and torque against tuned figures, then request your quote from Regal Tuning.`;
    document.querySelector('meta[name="description"]')?.setAttribute("content", desc);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_ORIGIN}/tuning/${data.make_slug}`;

    setJsonLd("mt-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
        { "@type": "ListItem", position: 3, name: data.make, item: `${SITE_ORIGIN}/tuning/${data.make_slug}` },
      ],
    });
    setJsonLd("mt-collection", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${data.make} Tuning & ECU Remapping`,
      url: `${SITE_ORIGIN}/tuning/${data.make_slug}`,
      isPartOf: { "@type": "WebSite", name: "Regal Tuning", url: `${SITE_ORIGIN}/` },
      about: { "@type": "Brand", name: data.make },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: data.vehicle_count,
        itemListElement: data.models.slice(0, 50).map((m, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: `${data.make} ${m.model}`,
          url: `${SITE_ORIGIN}${m.vehicles[0].url}`,
        })),
      },
    });
    return () => {
      removeJsonLd("mt-breadcrumb");
      removeJsonLd("mt-collection");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (miss) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="make-tuning-notfound">
        <div className="container-regal">
          <div className="border border-line bg-surface p-10 text-center">
            <Gauge size={28} className="mx-auto text-regal-amber" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Manufacturer not found</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">We don't have tuning coverage for that manufacturer yet — coverage grows continuously.</p>
            <Link to="/tuning-database" className="mt-6 inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="mt-notfound-db-link">
              Search the Tuning Database
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="make-tuning-loading">
        <div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div>
      </main>
    );
  }

  return (
    <main className="pt-32 sm:pt-40 pb-24" data-testid="make-tuning-page">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-database" className="hover:text-regal-amber" data-testid="mt-breadcrumb-db">Tuning Database</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{data.make}</span>
        </nav>

        <p className="overline-label mb-4">Manufacturer Tuning Hub</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="mt-h1">
          {data.make} <span className="text-regal-amber">Tuning</span> &amp; ECU Remapping
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="mt-intro">
          {`Verified ECU remapping gains for ${data.vehicle_count} ${data.make} vehicles across ${data.model_count} models${data.fuels?.length ? ` — covering ${data.fuels.join(" and ").toLowerCase()} powertrains` : ""}. Every vehicle page shows factory figures against Stage 1, Stage 2, Stage 3 and E85 outputs where available — compiled from Regal Tuning calibration records, established industry datasets and manufacturer specifications.`}
        </p>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="mt-stats">
          <span className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid="mt-stat-models">{data.model_count} models</span>
          <span className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid="mt-stat-vehicles">{data.vehicle_count} vehicles with published gains</span>
          {(data.fuels || []).map((f) => (
            <span key={f} className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid={`mt-fuel-${f.replace(/\W+/g, "-").toLowerCase()}`}>{f}</span>
          ))}
        </div>

        {/* MODEL INDEX */}
        <div className="mt-12" data-testid="mt-models">
          <p className="overline-label mb-6">{`Choose your ${data.make} model`}</p>
          <div className="space-y-8">
            {data.models.map((m, mi) => (
              <Reveal key={m.model_slug} delay={Math.min(mi * 0.03, 0.3)}>
                <section className="border border-line bg-white p-6 sm:p-8" data-testid={`mt-model-${m.model_slug}`}>
                  <h2 className="font-display text-lg sm:text-xl font-bold uppercase tracking-wide text-ink">
                    {data.make} <span className="text-regal-amber">{m.model}</span>
                    <span className="ml-3 font-mono text-[10px] font-normal uppercase tracking-[0.16em] text-neutral-400">{m.vehicles.length} variant{m.vehicles.length === 1 ? "" : "s"}</span>
                  </h2>
                  <ul className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                    {m.vehicles.map((v) => (
                      <li key={v.url}>
                        <Link to={v.url} className="group inline-flex items-baseline gap-2 text-sm text-neutral-500 hover:text-regal-amber transition-colors" data-testid={`mt-vehicle-link-${v.url.split("/").pop()}`}>
                          <ArrowUpRight size={12} className="shrink-0 translate-y-px text-regal-amber/60 group-hover:text-regal-amber" />
                          <span>{v.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              </Reveal>
            ))}
          </div>
        </div>

        {/* SEO CONTENT */}
        <div className="mt-16 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="overline-label mb-4">{`${data.make} remapping at Regal Tuning`}</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">Verified gains, engine by engine</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-400">
              {`Every ${data.make} calibration in this database is software-only Stage 1 upwards: no hardware changes, fully reversible, and developed against the specific engine variant rather than a generic map. Each vehicle page lists the exact factory output alongside the tuned figures for that engine and generation.`}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-neutral-400">
              Figures depend on vehicle health, fuel quality and conditions — we confirm the expected output for your specific vehicle in writing before any work begins. If your exact variant isn't listed, ask us: coverage expands continuously.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline-label mb-4">How to get your {data.make} remapped</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">From quote to tuned in days</h2>
            <p className="mt-4 text-sm leading-relaxed text-neutral-400">
              Request a quote with your vehicle details or registration and our calibration team will confirm support and expected gains. Existing customers upload their ECU file through the Regal Tuning file portal with full technical support and the original factory file always kept on record.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors" data-testid="mt-cta-portal">
                <FileUp size={15} /> Upload a File
              </a>
              <Link to={`/contact?subject=${encodeURIComponent(`Remap quote — ${data.make}`)}`} className="inline-flex items-center justify-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="mt-cta-quote">
                Request a Quote <ArrowUpRight size={15} />
              </Link>
            </div>
            <p className="mt-5">
              <Link to="/tuning-database" className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-regal-amber hover:text-amber-400 transition-colors" data-testid="mt-back-to-db">
                Search all manufacturers in the Tuning Gains Database <ArrowUpRight size={13} />
              </Link>
            </p>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
